import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  ApiParams,
  FilteredPayloadAction,
  LocationChangePayload,
  LocationState,
  reducerUtils,
} from '@wallaby-ui/core';
import {Action} from 'history';
import {pick} from 'lodash';
import {LOCATION_CHANGE} from 'redux-first-history';

import {FILTER_AND_SEARCH_REDUCER_STATE_ID} from './filter_and_search_constants';

interface ServiceUrls {
  featureName: string;
  filtersConfigPath: string;
  filterConfigService: string;
  searchService: string;
  searchDetailRoute: string;
  listRoute: string;
  resultTableConfig: string;
  resultDetailConfig: string;
  resultDetailProjectConfig: string;
  tablesConfigIds: string[];
  userFilterType: string;
}

export interface QueryParams {
  query: string;
  page: number;
  size: number;
  sort: string;
  filter: string;
  hierarchyId: string | null;
  parentHierarchyId: string | null;
  caseType?: string;
}

export interface FilterAndSearchState {
  componentsLoaded: Record<string, boolean>;
  dynamicResultItemsModels: Record<string, any>;
  isExportingCounter: number;
  exportRowsLimitReached: boolean;
  isLoadingCounter: number;
  locationState: LocationState;
  queryParameters: QueryParams;
  apiParameters: ApiParams;
  serviceUrls: Partial<ServiceUrls>;
}

export interface FilterAndSearchSearchPayload {
  options: Record<string, any>;
  response: any;
  dynamicResultItemsModels: Record<string, any>;
}

const initialState: FilterAndSearchState = {
  componentsLoaded: {},
  dynamicResultItemsModels: {},
  isExportingCounter: 0,
  exportRowsLimitReached: false,
  isLoadingCounter: 0,
  locationState: {
    locationParsed: false,
    reload: false,
  },
  queryParameters: {
    caseType: undefined,
    filter: '',
    hierarchyId: null,
    parentHierarchyId: null,
    query: '',
    page: 1,
    size: 10,
    sort: '',
  },
  apiParameters: {},
  serviceUrls: {
    featureName: undefined,
    filtersConfigPath: undefined,
    filterConfigService: undefined,
    searchService: undefined,
    searchDetailRoute: undefined,
    listRoute: undefined,
    resultTableConfig: undefined,
    resultDetailConfig: undefined,
    resultDetailProjectConfig: undefined,
    tablesConfigIds: undefined,
    userFilterType: undefined,
  },
};

export const filterAndSearchSlice = createSlice({
  name: FILTER_AND_SEARCH_REDUCER_STATE_ID,
  initialState,
  reducers: {
    locationParsed: {
      reducer(state, action: FilteredPayloadAction<boolean>) {
        if (state.locationState.locationParsed !== action.payload) {
          state.locationState.locationParsed = action.payload;
        }
      },
      prepare: reducerUtils.prepareWithStateIdFactory<boolean>(),
    },
    resetReload: {
      reducer(state, action: FilteredPayloadAction<boolean>) {
        if (state.locationState.reload !== action.payload) {
          state.locationState.reload = action.payload;
        }
      },
      prepare: reducerUtils.prepareWithStateIdFactory<boolean>(),
    },
    changeQueryParams: {
      reducer(state, action: FilteredPayloadAction<QueryParams>) {
        state.queryParameters = {...state.queryParameters, ...action.payload};
      },
      prepare: reducerUtils.prepareWithStateIdFactory<QueryParams>(),
    },
    changeApiParams: {
      reducer(state, action: FilteredPayloadAction<ApiParams>) {
        state.apiParameters = {
          ...state.apiParameters,
          ...action.payload,
        };
      },
      prepare: reducerUtils.prepareWithStateIdFactory<ApiParams>(),
    },
    resetQueryParams: {
      reducer(state, action: FilteredPayloadAction<string[]>) {
        state.queryParameters = {
          ...state.queryParameters,
          ...pick(initialState.queryParameters, action.payload),
        };
      },
      prepare: reducerUtils.prepareWithStateIdFactory<string[]>(),
    },
    search: {
      reducer(state) {
        state.isLoadingCounter += 1;
      },
      prepare: reducerUtils.prepareWithStateIdFactory<void>(),
    },
    searchFailure: {
      reducer(state) {
        state.isLoadingCounter -= 1;
      },
      prepare: reducerUtils.prepareWithStateIdFactory<void>(),
    },
    searchSuccess: {
      reducer(
        state,
        _action: FilteredPayloadAction<FilterAndSearchSearchPayload>
      ) {
        state.isLoadingCounter -= 1;
      },
      prepare:
        reducerUtils.prepareWithStateIdFactory<FilterAndSearchSearchPayload>(),
    },
    toggleLoaded: {
      reducer(state, action: FilteredPayloadAction<Record<string, boolean>>) {
        state.componentsLoaded = {...state.componentsLoaded, ...action.payload};
      },
      prepare:
        reducerUtils.prepareWithStateIdFactory<Record<string, boolean>>(),
    },
    initComponents: {
      reducer(state, action: FilteredPayloadAction<Record<string, boolean>>) {
        state.componentsLoaded = {...action.payload};
      },
      prepare:
        reducerUtils.prepareWithStateIdFactory<Record<string, boolean>>(),
    },
    changeServiceUrls: {
      reducer(state, action: FilteredPayloadAction<Record<string, boolean>>) {
        state.serviceUrls = {...state.serviceUrls, ...action.payload};
      },
      prepare:
        reducerUtils.prepareWithStateIdFactory<Record<string, boolean>>(),
    },
    setDynamicResultItems: {
      reducer(state, action: FilteredPayloadAction<Record<string, any>>) {
        state.dynamicResultItemsModels = {...action.payload};
      },
      prepare: reducerUtils.prepareWithStateIdFactory<Record<string, any>>(),
    },
    exportData: {
      reducer(state) {
        state.isExportingCounter += 1;
      },
      prepare: reducerUtils.prepareWithStateIdFactory<void>(),
    },
    exportDataFailure: {
      reducer(state) {
        state.isExportingCounter = Math.max(state.isExportingCounter - 1, 0);
      },
      prepare: reducerUtils.prepareWithStateIdFactory<void>(),
    },
    exportDataSuccess: {
      reducer(state) {
        state.isExportingCounter = Math.max(state.isExportingCounter - 1, 0);
      },
      prepare: reducerUtils.prepareWithStateIdFactory<void>(),
    },
    setExportRowsLimitReached: {
      reducer(state, action: FilteredPayloadAction<boolean>) {
        state.exportRowsLimitReached = action.payload;
      },
      prepare: reducerUtils.prepareWithStateIdFactory<boolean>(),
    },
  },
  extraReducers: {
    [LOCATION_CHANGE]: (
      state,
      action: PayloadAction<LocationChangePayload>
    ) => {
      const {action: locationAction, location} = action.payload;
      // TODO: handle value of reload in search parameter
      if (
        action.payload &&
        (locationAction === Action.Pop || location.search?.reload === 'true')
      ) {
        let newState = {...state};
        if (newState.locationState.locationParsed) {
          newState = {
            ...newState,
            locationState: {
              ...newState.locationState,
              reload: true,
              locationParsed: false,
            },
          };
        }
        return newState;
      }
      return state;
    },
  },
});
